import {ResourceStatus} from '@/shared/components/ui/resource-status'
import {SortableTableHeader} from '@cypro/ui/components/ui/sortable-table-header'
import {AccessorColDef, DisplayColDef} from '@cypro/ui/types'
import {chakra, Checkbox} from '@chakra-ui/react'
import {TradeName} from '@cypro/engine/schema'
import {createColumnHelper} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {LuPencil} from 'react-icons/lu'
import {useNavigate} from '@tanstack/react-router'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {useCan} from '@/shared/hooks/use-permissions'

const tradeNameColumnsHelper = createColumnHelper<TradeName>()

type AccessorColumnDef = AccessorColDef<TradeName>
type DisplayColumnDef = DisplayColDef<TradeName>

export const TradeNameColumnsDef = {
  viewPage: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'view=page',
      header: () => {
        return null
      },
      cell: ({row}) => {
        const navigate = useNavigate()
        const canEdit = useCan('catalogue.manage')
        if (!canEdit) return null

        const linkProps = {
          to: '/configuration/products/trade-names/new',
          search: {tradeNameId: row.original.id},
        } as const

        function onKeyDown(e: React.KeyboardEvent) {
          if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault()
            e.stopPropagation()
            navigate(linkProps)
          }
        }
        return (
          <LinkButton {...linkProps} variant="ghost" size="sm" onClick={e => e.stopPropagation()} onKeyDown={onKeyDown}>
            <LuPencil />{' '}
          </LinkButton>
        )
      },
      ...def,
    }),
  select: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'select',
      minSize: 42, // px
      header: ({table}) => {
        const allSelected = table.getIsAllRowsSelected()
        const isIndeterminate = table.getIsSomeRowsSelected() && !allSelected

        return (
          <Checkbox.Root
            size="md"
            aria-label="Select row"
            checked={isIndeterminate ? 'indeterminate' : allSelected}
            onCheckedChange={changes => table.toggleAllRowsSelected(Boolean(changes.checked))}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        )
      },
      cell: ({row}) => {
        return (
          <chakra.div onClick={e => e.stopPropagation()}>
            <Checkbox.Root
              size="md"
              aria-label="Select row"
              checked={row.getIsSelected()}
              onCheckedChange={changes => row.toggleSelected(Boolean(changes.checked))}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          </chakra.div>
        )
      },
      enableSorting: false,
      ...def,
    }),

  name: (def?: AccessorColumnDef) =>
    tradeNameColumnsHelper.accessor('name', {
      header: context => {
        const {t} = useTranslate('common')
        return <SortableTableHeader {...context}>{t('name')}</SortableTableHeader>
      },
      ...def,
    }),

  substances: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'concept',
      header: () => {
        const {t} = useTranslate('common')
        return t('associated_concept')
      },
      cell: ({row}) => {
        return <chakra.div color="fg.muted">{row.original?.medicationProfile?.drugCatalogItem?.name}</chakra.div>
      },
      ...def,
    }),

  form: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'medicationProfile',
      header: () => {
        const {t} = useTranslate('common')
        return t('form')
      },
      cell: ({row}) => {
        return (
          <chakra.div color="fg.muted">
            {row.original?.medicationProfile?.name || row.original?.medicationProfile?.medicineForm?.name}
          </chakra.div>
        )
      },
      ...def,
    }),

  type: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'type',
      header: () => {
        const {t} = useTranslate('common')
        return t('type')
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')
        const type = row.original?.medicationProfile?.drugCatalogItem?.catalogType
        if (!type) return null

        return <chakra.div color="fg.muted">{t(type)}</chakra.div>
      },
      ...def,
    }),

  strength: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'strength',
      header: () => {
        const {t} = useTranslate('common')
        return t('strength')
      },
      cell: ({row}) => {
        const strength = [row.original.strengthValue, row.original.strengthUnit?.ucumCode].filter(Boolean).join(' ')
        const volume = row.original.volumeValue ? `${row.original.volumeValue} mL` : ''

        return (
          <chakra.div color="fg.muted">
            {strength} {volume && ' / '} {volume}
          </chakra.div>
        )
      },
      ...def,
    }),

  stock: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.display({
      id: 'stock',
      header: () => {
        const {t} = useTranslate('common')
        return t('stock')
      },
      ...def,
    }),

  status: (def?: DisplayColumnDef) =>
    tradeNameColumnsHelper.accessor('status', {
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-end" width="100%">
            {t('status')}
          </chakra.div>
        )
      },
      cell: ({row}) => (
        <chakra.div display="flex" justifyContent="flex-end" width="100%">
          <ResourceStatus status={row.original.status} />
        </chakra.div>
      ),
      ...def,
    }),
}
